import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"
import ProgressBar from "../helpers/progress_bar"

const DEFAULT_ERROR_MESSAGE = "An internal error occurred. Please try again later."
// Bootstrap announces show.bs.modal through jQuery's trigger, which dispatches no DOM event.
const JQUERY_NAMESPACE = ".giftCancels"

// Backs the one #gift-cancel-modal shared by every "Cancel Gift" trigger on a page. The confirm
// link's own attributes decide what happens on click: a data-method link (subdomain/gifts/show,
// where there is only ever one gift on the page, so the href is already correct at render time)
// is left for Rails UJS to submit normally; a bare href (subdomain/gift_events/index, where one
// modal serves many rows and the href is only set from the triggering row) is canceled over
// fetch instead.
export default class extends Controller {
  static get targets() { return ["confirmButton"]; }

  connect() {
    this.onShow = this.onShow.bind(this);
    $(this.element).on(`show.bs.modal${JQUERY_NAMESPACE}`, this.onShow);
  }

  disconnect() {
    $(this.element).off(JQUERY_NAMESPACE);
  }

  // The triggering row's data-url is the only thing that tells the shared modal which gift it is
  // confirming - re-set on every open so a second row doesn't inherit the first row's target.
  // `event.relatedTarget` is the real DOM element Bootstrap's own show.bs.modal sets it to, even
  // though the event itself is a jQuery Event, so both sides here are plain DOM work.
  onShow(event) {
    const url = event.relatedTarget.dataset.url;
    this.confirmButtonTarget.setAttribute("href", url);
  }

  confirm(event) {
    event.preventDefault();

    const btn = event.currentTarget;
    const url = btn.getAttribute("href");
    const dataMethod = btn.getAttribute("data-method");

    btn.classList.add("disabled");

    if (dataMethod && url) {
      // Rails UJS submits a normal (non-AJAX) request for this link; nothing else to do.
      return;
    }

    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*"
    });

    fetch(url, options).
      then(response => response.json().then(data => ({ ok: response.ok, data }))).
      then(({ ok, data }) => {
        if (ok) {
          window.ltAlert({ message: data.message });
          this.reloadDatatable();
          $(this.element).modal("hide");
        } else {
          window.ltAlert({ message: data.message || DEFAULT_ERROR_MESSAGE, type: "toast-error" });
        }
      }).
      catch(this.logFailedCall).
      finally(() => btn.classList.remove("disabled"));
  }

  logFailedCall(response) {
    console.warn(response);
  }

  // Unscoped: the table this modal reloads belongs to the Recipient Activity page, outside this
  // controller's own mount, so there is no target to reach it through. The filter collection and
  // `data-source` read/write below are the same select2/DataTable-plugin shape as gift_events and
  // thank_yous' own reload() (decisions.md D8 for data-source; select2's `.data("select2")` /
  // `.select2("data")` are plugin state, not plain DOM) -- left on jQuery for the same reasons.
  reloadDatatable() {
    ProgressBar.start();

    const $datatable = $(".datatable");
    const filter = {};

    $(".datatable-moveable-filter, .datatable-filter").each(function() {
      const $select = $(this);
      const selectedOptions = $select.select2("data").map(option => option.id);
      filter[$select.attr("name").replace(/(^.*\[|\].*$)/g, "")] = selectedOptions;
    });

    $(".datatable-moveable-text-filter, .datatable-text-filter").each(function() {
      const $text = $(this);
      filter[$text.attr("name").replace(/(^.*\[|\].*$)/g, "")] = $text.val();
    });

    const params = Object.assign(this.currentParams($datatable), { filter });
    const replacement = `${$datatable.attr("data-source").split("?")[0]}?${$.param(params)}`;

    $datatable.attr("data-source", replacement);
    $datatable.DataTable().ajax.url(replacement).load(() => ProgressBar.stop());
  }

  // The table's data-source query string uses Rails' bracket notation
  // (filter[gift_status][]=...), which URLSearchParams does not reconstruct into the nested
  // objects `$.param` expects when re-serializing.
  currentParams($datatable) {
    let query = $datatable.attr("data-source");
    query = query.substring(query.indexOf("?") + 1);

    const decode = str => decodeURIComponent(str.replace(/\+/g, " "));
    const params = {};
    const regex = /([^&=]+)=?([^&]*)/g;
    let match;

    while ((match = regex.exec(query))) {
      const key = decode(match[1]);
      const value = decode(match[2]);

      if (key.slice(-2) === "[]") {
        const arrayKey = key.slice(0, -2);
        if (!params[arrayKey]) { params[arrayKey] = []; }
        params[arrayKey].push(value);
      } else {
        params[key] = value;
      }
    }

    Object.keys(params).forEach(prop => {
      const structure = prop.split("[");
      if (structure.length === 1) { return; }

      const levels = structure.map(part => part.replace(/[?[\]\\ ]/g, ""));
      this.assignNested(params, levels, params[prop]);
      delete params[prop];
    });

    return params;
  }

  assignNested(obj, keyPath, value) {
    let target = obj;
    for (let i = 0; i < keyPath.length - 1; i++) {
      const key = keyPath[i];
      if (!(key in target)) { target[key] = {}; }
      target = target[key];
    }
    target[keyPath[keyPath.length - 1]] = value;
  }
}
